import {
    CreateFacultyRequest,
    CreateModuleRequest,
    CreateQualificationLevelRequest,
    CreateQualificationRequest,
    CreateSchoolRequest,
    FacultyAPIModel,
    ICreateFacultyRequest,
    ICreateModuleRequest,
    ICreateQualificationLevelRequest,
    ICreateQualificationRequest,
    ICreateSchoolRequest,
    IUpdateFacultyRequest,
    IUpdateModuleRequest,
    IUpdateQualificationLevelRequest,
    IUpdateQualificationRequest,
    IUpdateSchoolRequest,
    ModuleAPIModel,
    QualificationAPIModel,
    QualificationLevelAPIModel,
    SchoolAPIModel,
    UpdateFacultyRequest,
    UpdateModuleRequest,
    UpdateQualificationLevelRequest,
    UpdateQualificationRequest,
    UpdateSchoolRequest,
} from "../api/APIClient";
import { structureClient } from "../api/clients";
import { unwrap, unwrapVoid } from "../api/errors";

/**
 * The academic catalogue: Faculty -> School -> Qualification, plus QualificationLevel and Module.
 *
 * Careful with the vocabulary here. A "School" is an academic school or department inside a faculty,
 * NOT the institution - the institution is the organization, which never appears in these payloads
 * because EF global query filters scope every read to it. A "Module" is a subject, independent of any
 * particular year; it becomes teachable when Management pairs it with a program level and a cycle.
 *
 * Every call goes through `unwrap`/`unwrapVoid`. These endpoints answer HTTP 200 with
 * `success: false` on failure, so an unguarded `.value` read renders an empty table and reports
 * nothing wrong - which is exactly the bug the old ManagementList screen carried a comment about.
 *
 * Deletes take the id as a query parameter rather than a route segment, and the parameter names are
 * inconsistent across controllers (camelCase here, PascalCase in Management). The generated client
 * hides that; do not hand-roll these URLs.
 */

export type { FacultyAPIModel, SchoolAPIModel, QualificationAPIModel, QualificationLevelAPIModel, ModuleAPIModel };

// ------------------------------------------------------------------ faculties

export const listFaculties = (): Promise<FacultyAPIModel[]> =>
    structureClient.listFaculties().then((r) => unwrap(r) ?? []);

export const getFaculty = (id: string): Promise<FacultyAPIModel | undefined> => structureClient.getFaculty(id).then(unwrap);

export const createFaculty = (model: ICreateFacultyRequest): Promise<void> =>
    structureClient.createFaculty(new CreateFacultyRequest(model)).then(unwrapVoid);

export const updateFaculty = (model: IUpdateFacultyRequest): Promise<void> =>
    structureClient.updateFaculty(new UpdateFacultyRequest(model)).then(unwrapVoid);

export const deleteFaculty = (id: string): Promise<void> => structureClient.deleteFaculty(id).then(unwrapVoid);

// -------------------------------------------------------------------- schools

export const listSchools = (): Promise<SchoolAPIModel[]> => structureClient.listSchools().then((r) => unwrap(r) ?? []);

export const createSchool = (model: ICreateSchoolRequest): Promise<void> =>
    structureClient.createSchool(new CreateSchoolRequest(model)).then(unwrapVoid);

export const updateSchool = (model: IUpdateSchoolRequest): Promise<void> =>
    structureClient.updateSchool(new UpdateSchoolRequest(model)).then(unwrapVoid);

export const deleteSchool = (id: string): Promise<void> => structureClient.deleteSchool(id).then(unwrapVoid);

// ------------------------------------------------------------- qualifications

export const listQualifications = (): Promise<QualificationAPIModel[]> =>
    structureClient.listQualifications().then((r) => unwrap(r) ?? []);

export const createQualification = (model: ICreateQualificationRequest): Promise<void> =>
    structureClient.createQualification(new CreateQualificationRequest(model)).then(unwrapVoid);

export const updateQualification = (model: IUpdateQualificationRequest): Promise<void> =>
    structureClient.updateQualification(new UpdateQualificationRequest(model)).then(unwrapVoid);

export const deleteQualification = (id: string): Promise<void> => structureClient.deleteQualification(id).then(unwrapVoid);

// ------------------------------------------------------- qualification levels

export const listQualificationLevels = (): Promise<QualificationLevelAPIModel[]> =>
    structureClient.listQualificationLevels().then((r) => unwrap(r) ?? []);

export const createQualificationLevel = (model: ICreateQualificationLevelRequest): Promise<void> =>
    structureClient.createQualificationLevel(new CreateQualificationLevelRequest(model)).then(unwrapVoid);

export const updateQualificationLevel = (model: IUpdateQualificationLevelRequest): Promise<void> =>
    structureClient.updateQualificationLevel(new UpdateQualificationLevelRequest(model)).then(unwrapVoid);

export const deleteQualificationLevel = (id: string): Promise<void> =>
    structureClient.deleteQualificationLevel(id).then(unwrapVoid);

// -------------------------------------------------------------------- modules

export const listModules = (): Promise<ModuleAPIModel[]> => structureClient.listModules().then((r) => unwrap(r) ?? []);

export const createModule = (model: ICreateModuleRequest): Promise<void> =>
    structureClient.createModule(new CreateModuleRequest(model)).then(unwrapVoid);

export const updateModule = (model: IUpdateModuleRequest): Promise<void> =>
    structureClient.updateModule(new UpdateModuleRequest(model)).then(unwrapVoid);

export const deleteModule = (id: string): Promise<void> => structureClient.deleteModule(id).then(unwrapVoid);
